import { useEffect, useState } from 'react';
import { Building2, LayoutGrid, List, Merge, Phone, Plus, Search, Star, Users, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { callBackend, useMutation, usePaginatedQuery } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import { useCrm } from './CrmContext';
import { CompanyEditor } from './CompanyPanel';
import { buttonClass, errorMessage, formatWhen, inputClass } from './format';
import { LIFECYCLE_LABELS, type CrmCompany } from './types';

type Company = CrmCompany & { statsIncomplete?: boolean };
type Sort = 'name' | 'calls' | 'recent' | 'contacts';

export function CompaniesTab() {
  const { data, openCompany } = useCrm();
  const mergeCompanies = useMutation(backendApi.crm.mergeCompanies);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [sort, setSort] = useState<Sort>('name');
  const [view, setView] = useState<'tiles' | 'list'>('tiles');
  const [columns, setColumns] = useState<1 | 2 | 3>(3);
  const [ranked, setRanked] = useState<{ key: string; rows: Company[]; done: boolean; error?: string } | null>(null);
  const [adding, setAdding] = useState(false);
  const [mergeMode, setMergeMode] = useState(false);
  const [selected, setSelected] = useState<Company[]>([]);
  const [primaryId, setPrimaryId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);
  const companies = usePaginatedQuery<Company>(backendApi.crm.listCompaniesPage, { search: debounced || undefined }, 50);
  const refreshMarker = companies.isLoading ? null : companies.items;
  const sortKey = `${sort}\n${debounced}`;
  useEffect(() => {
    if (sort === 'name' || !refreshMarker) return;
    let cancelled = false;
    const fetchAll = async () => {
      const rows: Company[] = [];
      let cursor: string | null = null;
      let done = false;
      setRanked({ key: sortKey, rows: [], done: false });
      try {
        while (!done && !cancelled) {
          const page: { page: Company[]; isDone: boolean; continueCursor: string } = await callBackend(backendApi.crm.listCompaniesPage, { search: debounced || undefined, paginationOpts: { numItems: 50, cursor } });
          rows.push(...page.page);
          done = page.isDone;
          if (!done && (!page.continueCursor || page.continueCursor === cursor)) throw new Error('Unable to advance the company list');
          cursor = page.continueCursor;
          if (!cancelled) setRanked({ key: sortKey, rows: [...rows], done });
        }
      } catch (error) {
        if (!cancelled) setRanked({ key: sortKey, rows: [], done: true, error: errorMessage(error) });
      }
    };
    void fetchAll();
    return () => { cancelled = true; };
  }, [sort, sortKey, debounced, refreshMarker]);
  const rankedCurrent = ranked?.key === sortKey ? ranked : null;
  const visible = sort === 'name' ? companies.items : [...(rankedCurrent?.rows ?? [])].sort((a, b) => {
    const difference = sort === 'calls' ? b.stats.totalInteractions - a.stats.totalInteractions : sort === 'recent' ? (b.stats.lastInteractionTimestamp ?? 0) - (a.stats.lastInteractionTimestamp ?? 0) : b.contactCount - a.contactCount;
    return difference || a.name.localeCompare(b.name);
  });
  const loading = sort === 'name' ? companies.isLoading : !rankedCurrent || !rankedCurrent.done;
  const error = sort === 'name' ? companies.error : rankedCurrent?.error;

  const merge = async () => {
    if (!primaryId) return;
    try {
      await mergeCompanies({ primaryId, otherIds: selected.filter((company) => company.id !== primaryId).map((company) => company.id) });
      toast.success('Companies merged');
      setPrimaryId(null);
      setSelected([]);
      setMergeMode(false);
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  return (
    <Card animate={false}>
      <div className="flex flex-col lg:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search companies or tags" className={`${inputClass} pl-9`} />
        </div>
        <select value={sort} onChange={(event) => setSort(event.target.value as Sort)} className={`${inputClass} lg:w-48`} aria-label="Sort companies">
          <option value="name">Name (A–Z)</option>
          <option value="calls">Most calls</option>
          <option value="recent">Recent activity</option>
          <option value="contacts">Most people</option>
        </select>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden" aria-label="Company view">
            <button type="button" aria-label="Tile view" aria-pressed={view === 'tiles'} onClick={() => { setView('tiles'); setColumns(3); }} className={`p-2 ${view === 'tiles' ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300' : 'text-gray-500'}`}><LayoutGrid className="w-4 h-4" /></button>
            <button type="button" aria-label="List view" aria-pressed={view === 'list'} onClick={() => { setView('list'); setColumns(1); }} className={`p-2 ${view === 'list' ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300' : 'text-gray-500'}`}><List className="w-4 h-4" /></button>
          </div>
          <select value={columns} onChange={(event) => setColumns(Number(event.target.value) as 1 | 2 | 3)} className={`${inputClass} w-32`} aria-label="Number of columns">
            <option value={1}>1 column</option><option value={2}>2 columns</option><option value={3}>3 columns</option>
          </select>
        </div>
        {data?.canManage && (
          <div className="flex gap-2">
            <button type="button" onClick={() => setAdding(true)} className={buttonClass.primary}>
              <Plus className="w-4 h-4" /> Company
            </button>
            {mergeMode ? (
              <>
                <button type="button" disabled={selected.length < 2} onClick={() => setPrimaryId(selected[0].id)} className={buttonClass.primary}>
                  <Merge className="w-4 h-4" /> Merge {selected.length}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMergeMode(false);
                    setSelected([]);
                  }}
                  className={buttonClass.secondary}
                >
                  <X className="w-4 h-4" />
                </button>
              </>
            ) : (
              <button type="button" onClick={() => setMergeMode(true)} className={buttonClass.secondary}>
                <Merge className="w-4 h-4" /> Merge
              </button>
            )}
          </div>
        )}
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}
      {loading && <p className="text-gray-500 text-sm mb-3" role="status">{sort === 'name' ? 'Loading…' : `Sorting companies… ${visible.length} checked`}</p>}
      {sort !== 'name' && visible.some((company) => company.statsIncomplete) && <p className="text-amber-700 dark:text-amber-400 text-sm mb-3">Some company totals are incomplete, so their position may be approximate.</p>}
      {!loading && !error && (sort !== 'name' || companies.isDone) && visible.length === 0 && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <Building2 className="w-10 h-10 mx-auto mb-2 opacity-60" />
          No companies{search ? ' match' : ' yet'}.
        </div>
      )}
      <div className={`grid grid-cols-1 ${columns === 2 ? 'md:grid-cols-2' : columns === 3 ? 'md:grid-cols-2 xl:grid-cols-3' : ''} ${view === 'list' ? 'gap-2' : 'gap-4'}`}>
        {visible.map((company) => (
          <div key={company.id} className="relative rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-primary-400 transition-colors">
            {mergeMode && (
              <label className="absolute top-4 right-4 z-10 flex items-center gap-1 text-xs text-gray-500">
              <input
                type="checkbox"
                checked={selected.some((item) => item.id === company.id)}
                onChange={() => setSelected((current) => (current.some((item) => item.id === company.id) ? current.filter((item) => item.id !== company.id) : [...current, company]))}
                className="w-4 h-4 rounded"
                aria-label={`Select ${company.name}`}
              />
              Select</label>
            )}
            <button type="button" onClick={() => openCompany(company.id)} className={`w-full text-left rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 ${view === 'list' ? 'p-3' : 'p-4'}`}>
              {view === 'list' ? <div className={`flex items-center gap-3 ${mergeMode ? 'pr-16' : ''}`}>
                <span className="w-9 h-9 shrink-0 rounded-lg bg-primary-50 dark:bg-primary-900/40 text-primary-600 dark:text-primary-300 flex items-center justify-center"><Building2 className="w-4 h-4" /></span>
                <span className="min-w-0 flex-1"><span className="block font-semibold text-gray-900 dark:text-white truncate">{company.vip && <Star className="inline w-3.5 h-3.5 mr-1 -mt-0.5 text-amber-500 fill-amber-400" />}{company.name}</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">{[company.lifecycle ? LIFECYCLE_LABELS[company.lifecycle] : null, `${company.contactCount}${company.statsIncomplete ? '+' : ''} people`, `${company.stats.totalInteractions}${company.statsIncomplete ? '+' : ''} calls`, company.stats.lastInteractionTimestamp ? `last ${formatWhen(company.stats.lastInteractionTimestamp * 1000)}` : 'no calls yet', company.ownerName || company.ownerExtension ? `owner ${company.ownerName ?? company.ownerExtension}` : null].filter(Boolean).join(' · ')}</span>
                </span>
              </div> : <>
              <div className={`flex items-start gap-3 ${mergeMode ? 'pr-16' : ''}`}>
                <span className="w-10 h-10 shrink-0 rounded-lg bg-primary-50 dark:bg-primary-900/40 text-primary-600 dark:text-primary-300 flex items-center justify-center"><Building2 className="w-5 h-5" /></span>
                <span className="min-w-0"><span className="block font-semibold text-gray-900 dark:text-white truncate">{company.vip && <Star className="inline w-3.5 h-3.5 mr-1 -mt-0.5 text-amber-500 fill-amber-400" />}{company.name}</span><span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">{company.lifecycle ? LIFECYCLE_LABELS[company.lifecycle] : 'No stage set'}</span></span>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-4 text-sm text-gray-700 dark:text-gray-200">
                <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-gray-400" /> {company.contactCount}{company.statsIncomplete ? '+' : ''} {company.contactCount === 1 ? 'person' : 'people'}</span>
                <span className="flex items-center gap-1.5"><Phone className="w-4 h-4 text-gray-400" /> {company.stats.totalInteractions}{company.statsIncomplete ? '+' : ''} calls</span>
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 space-y-1">
                <p>Last activity: {company.stats.lastInteractionTimestamp ? formatWhen(company.stats.lastInteractionTimestamp * 1000) : 'No calls yet'}</p>
                <p>Owner: {company.ownerName ?? company.ownerExtension ?? 'Unassigned'}{company.stats.missedCalls ? ` · ${company.stats.missedCalls} missed` : ''}</p>
              </div>
              </>}
            </button>
          </div>
        ))}
      </div>
      {sort === 'name' && !companies.isLoading && !companies.error && !companies.isDone && <button type="button" onClick={companies.loadMore} disabled={companies.loadingMore} className={`${buttonClass.secondary} mt-4`}>{companies.loadingMore ? 'Loading…' : 'Load more'}</button>}

      {adding && <CompanyEditor onClose={() => setAdding(false)} onSaved={(id) => openCompany(id)} />}
      {primaryId && (
        <Modal isOpen onClose={() => setPrimaryId(null)} title="Merge companies">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">Keep one; the others' people, calls, notes and tasks move onto it.</p>
          <div className="space-y-2 mb-6">
            {selected.map((company) => {
              const id = company.id;
              return (
                <label key={id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${primaryId === id ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-200 dark:border-gray-700'}`}>
                  <input type="radio" checked={primaryId === id} onChange={() => setPrimaryId(id)} />
                  <span className="text-gray-900 dark:text-white">
                    {company?.name} <span className="text-xs text-gray-500">({company?.contactCount} people)</span>
                  </span>
                </label>
              );
            })}
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setPrimaryId(null)} className={buttonClass.secondary}>
              Cancel
            </button>
            <button type="button" onClick={() => void merge()} className={buttonClass.primary}>
              Merge
            </button>
          </div>
        </Modal>
      )}
    </Card>
  );
}
